/**
 * ChatPanel — the unified "AI chat hub" panel.
 *
 * Replaces the old separate per-service panels (chat-claude / chat-chatgpt /
 * chat-gemini). One floating, project-independent panel holds many tabs: a few
 * built-in presets plus any custom URL the user saves. Tab config lives in the
 * GLOBAL chatStore (persisted, never per-project), so switching projects never
 * reloads or resets a chat or its history.
 *
 * Each tab is an embedded CEF host browser keyed by a global label `chat-<id>`.
 * We mount a pane for every tab and toggle `forceHidden` instead of unmounting,
 * so switching tabs is instant and never reloads. A never-activated preset is
 * lazy — its browser isn't created until the tab is first shown.
 */
import { useEffect, useRef, useState } from 'react';
import { MessagesSquare, Plus, X, Globe, ExternalLink } from 'lucide-react';
import { useBackend, isCefHost, CefBackend } from '@/services/backend';
import type { HostBrowserControl } from '@/services/backend/cefBrowser';
import { useUIStore } from '@/stores/uiStore';
import { useHostBrowser } from '@/lib/useBrowserWebview';
import { useChatStore, CHAT_PRESETS, type ChatTabConfig } from '@/stores/chatStore';

/** One embedded host browser pinned to a global label + the tab's url. Stays
 *  mounted while hidden so the session/history persists across tab switches. */
function ChatHostPane({ tab, active, ctl }: { tab: ChatTabConfig; active: boolean; ctl: HostBrowserControl }) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const overlayDepth = useUIStore((s) => s.overlayDepth);
  const label = `chat-${tab.id}`;
  useHostBrowser(ctl, {
    label,
    url: tab.url,
    anchorRef,
    // Inactive tabs (and any open overlay/menu) host-hide rather than unmount.
    forceHidden: !active || overlayDepth > 0,
  });
  return (
    <div
      ref={anchorRef}
      className="absolute inset-0 w-full h-full bg-white"
      style={{ display: active ? 'block' : 'none' }}
      data-browser-anchor={label}
    />
  );
}

export interface ChatPanelProps {
  /** Back-compat: the old per-service chat panels pass their service slug so an
   *  upgraded layout opens the unified panel focused on that service. */
  initialService?: string;
}

export default function ChatPanel({ initialService }: ChatPanelProps) {
  const backend = useBackend();
  const hostCtl: HostBrowserControl | null =
    isCefHost() && backend instanceof CefBackend ? backend.browser : null;

  const tabs = useChatStore((s) => s.tabs);
  const activeTabId = useChatStore((s) => s.activeTabId);
  const setActive = useChatStore((s) => s.setActive);
  const addPreset = useChatStore((s) => s.addPreset);
  const addCustom = useChatStore((s) => s.addCustom);
  const removeTab = useChatStore((s) => s.removeTab);

  const [menuOpen, setMenuOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [cName, setCName] = useState('');
  const [cUrl, setCUrl] = useState('');

  // Back-compat: old per-service chat panels (chat-claude/…) focus that preset.
  useEffect(() => {
    if (initialService) addPreset(initialService);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialService]);

  // An open menu/form is a React overlay above the OSR browser, so it must hide
  // the embedded browser (same overlayDepth pattern modals use) or it'd render
  // behind it.
  const overlayActive = menuOpen || customOpen;
  useEffect(() => {
    if (!overlayActive) return;
    const { pushOverlay, popOverlay } = useUIStore.getState();
    pushOverlay();
    return () => popOverlay();
  }, [overlayActive]);

  const presetsToAdd = CHAT_PRESETS.filter((p) => !tabs.some((t) => t.id === p.id));

  const closeMenu = () => {
    setMenuOpen(false);
    setCustomOpen(false);
  };
  const submitCustom = () => {
    if (!cUrl.trim()) return;
    addCustom(cName, cUrl);
    setCName('');
    setCUrl('');
    closeMenu();
  };

  // Web fallback (no CEF host): chat services block iframing, so offer external.
  if (!hostCtl) {
    const active = tabs.find((t) => t.id === activeTabId) ?? tabs[0];
    return (
      <div className="flex flex-col h-full items-center justify-center bg-base-100 text-base-content/70 gap-4">
        <p className="text-sm">Chats open as embedded browsers in the desktop app.</p>
        {active && (
          <button type="button" className="btn btn-primary btn-sm gap-1" onClick={() => window.open(active.url, '_blank')}>
            <ExternalLink className="w-4 h-4" /> Open {active.name}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-base-100">
      {/* Tab strip */}
      <div className="flex items-center gap-1 px-1.5 py-1 border-b border-base-300 bg-base-200/50 overflow-x-auto flex-shrink-0">
        {tabs.map((t) => (
          <div
            key={t.id}
            onClick={() => setActive(t.id)}
            className={`group flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-md text-xs cursor-pointer whitespace-nowrap select-none ${
              t.id === activeTabId
                ? 'bg-base-100 text-base-content shadow-sm font-medium'
                : 'text-base-content/70 hover:bg-base-300/60'
            }`}
          >
            <span>{t.name}</span>
            <button
              type="button"
              title="Close tab"
              className="opacity-0 group-hover:opacity-100 hover:bg-base-300 rounded p-0.5 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                removeTab(t.id);
              }}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}

        {/* "+" add menu */}
        <div className="relative flex-shrink-0">
          <button
            type="button"
            title="Add chat"
            className="btn btn-ghost btn-xs px-1"
            onClick={() => {
              setCustomOpen(false);
              setMenuOpen((v) => !v);
            }}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          {menuOpen && (
            <>
              {/* click-away */}
              <div className="fixed inset-0 z-40" onClick={closeMenu} />
              <div className="absolute right-0 top-full mt-1 z-50 w-56 p-1 shadow-lg bg-base-200 border border-base-300 rounded-lg">
                {!customOpen ? (
                  <>
                    {presetsToAdd.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-base-300 rounded-md"
                        onClick={() => {
                          addPreset(p.id);
                          closeMenu();
                        }}
                      >
                        <MessagesSquare className="w-4 h-4" /> {p.name}
                      </button>
                    ))}
                    {presetsToAdd.length > 0 && <div className="h-px bg-base-300 my-1" />}
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-base-300 rounded-md"
                      onClick={() => setCustomOpen(true)}
                    >
                      <Globe className="w-4 h-4" /> Custom URL…
                    </button>
                  </>
                ) : (
                  <div className="p-2 flex flex-col gap-2">
                    <input
                      autoFocus
                      value={cName}
                      onChange={(e) => setCName(e.target.value)}
                      placeholder="Name (e.g. DeepSeek)"
                      className="input input-xs input-bordered w-full"
                    />
                    <input
                      value={cUrl}
                      onChange={(e) => setCUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitCustom();
                        if (e.key === 'Escape') closeMenu();
                      }}
                      placeholder="https://…"
                      className="input input-xs input-bordered w-full"
                    />
                    <div className="flex justify-end gap-1">
                      <button type="button" className="btn btn-ghost btn-xs" onClick={() => setCustomOpen(false)}>
                        Back
                      </button>
                      <button type="button" className="btn btn-primary btn-xs" onClick={submitCustom} disabled={!cUrl.trim()}>
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Panes */}
      <div className="relative flex-1 min-h-0">
        {tabs.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-base-content/60">
            No chats open — click <Plus className="w-3.5 h-3.5 mx-1 inline" /> to add one.
          </div>
        ) : (
          tabs.map((t) => <ChatHostPane key={t.id} tab={t} active={t.id === activeTabId} ctl={hostCtl} />)
        )}
      </div>
    </div>
  );
}
